import { create } from "zustand";
import type { Package, PackageItem, PackageCollaborator, Operator } from "../types/database";

interface PackagesState {
  // Packages
  packages: Package[];
  selectedPackage: Package | null;
  
  // Current package editing
  currentPackageItems: PackageItem[];
  currentCollaborators: PackageCollaborator[];
  
  // Calendar selection
  selectedDate: Date | null;
  calendarView: 'month' | 'week' | 'day';
  
  // UI State
  isCreating: boolean;
  isEditing: boolean;
  showPdfPreview: boolean;
  
  // Operator selection for adding to package
  selectedOperatorForPackage: Operator | null;
  
  // Actions
  setPackages: (packages: Package[]) => void;
  addPackage: (pkg: Package) => void;
  updatePackage: (id: string, updates: Partial<Package>) => void;
  removePackage: (id: string) => void;
  setSelectedPackage: (pkg: Package | null) => void;
  
  // Package items
  setCurrentPackageItems: (items: PackageItem[]) => void;
  addPackageItem: (item: PackageItem) => void;
  updatePackageItem: (id: string, updates: Partial<PackageItem>) => void;
  removePackageItem: (id: string) => void;
  reorderPackageItems: (items: PackageItem[]) => void;
  
  // Collaborators
  setCurrentCollaborators: (collaborators: PackageCollaborator[]) => void;
  addCollaborator: (collaborator: PackageCollaborator) => void;
  updateCollaborator: (id: string, updates: Partial<PackageCollaborator>) => void;
  removeCollaborator: (id: string) => void;
  
  // Calendar
  setSelectedDate: (date: Date | null) => void;
  setCalendarView: (view: 'month' | 'week' | 'day') => void;
  
  // UI
  setIsCreating: (isCreating: boolean) => void;
  setIsEditing: (isEditing: boolean) => void;
  setShowPdfPreview: (show: boolean) => void;
  setSelectedOperatorForPackage: (operator: Operator | null) => void;
  
  // Reset
  resetCurrentPackage: () => void;
}

export const usePackagesStore = create<PackagesState>((set) => ({
  // Initial state
  packages: [],
  selectedPackage: null,
  currentPackageItems: [],
  currentCollaborators: [],
  selectedDate: null,
  calendarView: 'month',
  isCreating: false,
  isEditing: false,
  showPdfPreview: false,
  selectedOperatorForPackage: null,
  
  // Packages actions
  setPackages: (packages) => set({ packages }),
  
  addPackage: (pkg) => set((state) => ({ 
    packages: [pkg, ...state.packages] 
  })),
  
  updatePackage: (id, updates) => set((state) => ({
    packages: state.packages.map((p) =>
      p.id === id ? { ...p, ...updates } : p
    ),
    selectedPackage: state.selectedPackage?.id === id 
      ? { ...state.selectedPackage, ...updates } 
      : state.selectedPackage,
  })),
  
  removePackage: (id) => set((state) => ({
    packages: state.packages.filter((p) => p.id !== id),
    selectedPackage: state.selectedPackage?.id === id ? null : state.selectedPackage,
  })),
  
  setSelectedPackage: (pkg) => set({ 
    selectedPackage: pkg,
    currentPackageItems: pkg?.items || [],
    currentCollaborators: pkg?.collaborators || [],
  }),
  
  // Package items actions
  setCurrentPackageItems: (items) => set({ currentPackageItems: items }),
  
  addPackageItem: (item) => set((state) => ({
    currentPackageItems: [...state.currentPackageItems, item],
    // Also update selectedPackage.items to keep UI in sync
    selectedPackage: state.selectedPackage ? {
      ...state.selectedPackage,
      items: [...(state.selectedPackage.items || []), item],
    } : null,
  })),
  
  updatePackageItem: (id, updates) => set((state) => ({
    currentPackageItems: state.currentPackageItems.map((item) =>
      item.id === id ? { ...item, ...updates } : item
    ),
    // Also update selectedPackage.items to keep UI in sync
    selectedPackage: state.selectedPackage ? {
      ...state.selectedPackage,
      items: state.selectedPackage.items?.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      ),
    } : null,
  })),
  
  removePackageItem: (id) => set((state) => ({
    currentPackageItems: state.currentPackageItems.filter((item) => item.id !== id),
    // Also update selectedPackage.items to keep UI in sync
    selectedPackage: state.selectedPackage ? {
      ...state.selectedPackage,
      items: state.selectedPackage.items?.filter((item) => item.id !== id),
    } : null,
  })),
  
  reorderPackageItems: (items) => set({ currentPackageItems: items }),
  
  // Collaborators actions
  setCurrentCollaborators: (collaborators) => set({ currentCollaborators: collaborators }),
  
  addCollaborator: (collaborator) => set((state) => ({
    currentCollaborators: [...state.currentCollaborators, collaborator],
    // Also update selectedPackage.collaborators for immediate UI update
    selectedPackage: state.selectedPackage && state.selectedPackage.id === collaborator.package_id
      ? {
          ...state.selectedPackage,
          collaborators: [...(state.selectedPackage.collaborators || []), collaborator],
        }
      : state.selectedPackage,
  })),
  
  updateCollaborator: (id, updates) => set((state) => ({
    currentCollaborators: state.currentCollaborators.map((c) =>
      c.id === id ? { ...c, ...updates } : c
    ),
  })),
  
  removeCollaborator: (id) => set((state) => ({
    currentCollaborators: state.currentCollaborators.filter((c) => c.id !== id),
  })),
  
  // Calendar actions
  setSelectedDate: (date) => set({ selectedDate: date }),
  setCalendarView: (view) => set({ calendarView: view }),
  
  // UI actions
  setIsCreating: (isCreating) => set({ isCreating }),
  setIsEditing: (isEditing) => set({ isEditing }),
  setShowPdfPreview: (show) => set({ showPdfPreview: show }),
  setSelectedOperatorForPackage: (operator) => set({ selectedOperatorForPackage: operator }),
  
  // Reset
  resetCurrentPackage: () => set({
    selectedPackage: null,
    currentPackageItems: [],
    currentCollaborators: [],
    isCreating: false,
    isEditing: false,
  }),
}));

